import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead,
  Box,
  Autocomplete,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useIntl } from 'react-intl'
import { CloseIcon } from '../../../components/icons/CloseIcon'
import { colorPalette } from '../../../style/colorPalette'
import Workload, {
  WorkloadUnit,
  getWorkloadUnitValue,
} from '../../../../lib/functions/workload'
import { UNASSIGNED_ASSIGNEE_ID } from '../hooks/estimatesByAssignee'

interface AssigneeEstimate {
  assigneeId: string
  assigneeName: string
  totalEstimate: number
  taskCount: number
}

interface SprintEstimate {
  sprintId: string
  sprintName: string
  assigneeEstimates: AssigneeEstimate[]
  totalEstimate: number
  totalTasks: number
}

interface EstimatesByAssigneePopupProps {
  open: boolean
  onClose: () => void
  getSprintEstimates: (includeFinishedAndCanceled?: boolean) => SprintEstimate[]
  initialSprintId?: string
  data: any[] // ProductBacklogRow[]
  dataVersion?: number // 追加: データの変更を追跡するためのバージョン番号
  workloadUnit: WorkloadUnit // 工数単位
  workloadStandard: { dailyWorkHours: number; monthlyWorkDays: number } // 工数換算基準
}

interface MemberEstimatedHoursPerSprintTableProps {
  sprint: SprintEstimate
  isLast: boolean
  workloadUnit: WorkloadUnit
  workloadStandard: { dailyWorkHours: number; monthlyWorkDays: number }
}

const TableHeaderCell = styled(TableCell)({
  fontSize: '14px',
  fontWeight: 'bold',
  color: colorPalette.monotone[10],
  padding: '10px 16px',
  backgroundColor: colorPalette.monotone[1],
})

const TableDataCell = styled(TableCell)({
  fontSize: '14px',
  color: colorPalette.monotone[10],
  padding: '10px 16px',
})

const MemberEstimatedHoursPerSprintTable: React.FC<
  MemberEstimatedHoursPerSprintTableProps
> = ({ sprint, isLast, workloadUnit, workloadStandard }) => {
  const intl = useIntl()

  // 工数単位のラベルを取得
  const getWorkloadUnitLabel = (unit: WorkloadUnit): string => {
    switch (unit) {
      case WorkloadUnit.HOUR:
        return intl.formatMessage({ id: 'workloadUnit.hour' })
      case WorkloadUnit.DAY:
        return intl.formatMessage({ id: 'workloadUnit.day' })
      case WorkloadUnit.MONTH:
        return intl.formatMessage({ id: 'workloadUnit.month' })
      default:
        return intl.formatMessage({ id: 'workloadUnit.hour' })
    }
  }

  // 時間単位の工数を選択された単位に変換
  const convertHoursToWorkloadUnit = (hours: number): number => {
    const workload = Workload.from({
      hour: hours,
      standard: workloadStandard,
    })
    return getWorkloadUnitValue(workload, workloadUnit)
  }

  const convertedTotalEstimate = convertHoursToWorkloadUnit(
    sprint.totalEstimate
  )

  return (
    <Box key={sprint.sprintId} sx={{ mb: isLast ? 0 : 3 }}>
      <Table size="small" sx={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '40%' }} />
          <col style={{ width: '35%' }} />
          <col style={{ width: '25%' }} />
        </colgroup>
        <TableHead>
          <TableRow>
            <TableHeaderCell>
              {intl.formatMessage({
                id: 'productBacklog.estimatesByAssignee.assigneeName',
              })}
            </TableHeaderCell>
            <TableHeaderCell align="right">
              {intl.formatMessage({
                id: 'productBacklog.estimatesByAssignee.estimate',
              })}
              ({getWorkloadUnitLabel(workloadUnit)})
            </TableHeaderCell>
            <TableHeaderCell align="right">
              {intl.formatMessage({
                id: 'productBacklog.estimatesByAssignee.taskCount',
              })}
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* 総計行 */}
          <TableRow
            sx={{
              backgroundColor: colorPalette.blue[0],
            }}
          >
            <TableDataCell
              sx={{
                fontWeight: 'bold',
                color: colorPalette.monotone[10],
              }}
            >
              {intl.formatMessage({
                id: 'productBacklog.estimatesByAssignee.total',
              })}
            </TableDataCell>
            <TableDataCell
              align="right"
              sx={{
                fontWeight: 'bold',
                color: colorPalette.monotone[10],
              }}
            >
              {convertedTotalEstimate.toFixed(2)}
            </TableDataCell>
            <TableDataCell
              align="right"
              sx={{
                fontWeight: 'bold',
                color: colorPalette.monotone[10],
              }}
            >
              {sprint.totalTasks}
              {intl.formatMessage({
                id: 'productBacklog.estimatesByAssignee.taskCountUnit',
              })}
            </TableDataCell>
          </TableRow>
          {sprint.assigneeEstimates.map(estimate => {
            const convertedEstimate = convertHoursToWorkloadUnit(
              estimate.totalEstimate
            )
            const isUnassigned = estimate.assigneeId === UNASSIGNED_ASSIGNEE_ID
            return (
              <TableRow
                key={estimate.assigneeId}
                hover={true}
                sx={
                  isUnassigned
                    ? {
                        backgroundColor: colorPalette.pink[1],
                      }
                    : undefined
                }
              >
                <TableDataCell>{estimate.assigneeName}</TableDataCell>
                <TableDataCell align="right">
                  {convertedEstimate.toFixed(2)}
                </TableDataCell>
                <TableDataCell align="right">
                  {estimate.taskCount}
                  {intl.formatMessage({
                    id: 'productBacklog.estimatesByAssignee.taskCountUnit',
                  })}
                </TableDataCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

const PopupContainer = styled('div')(() => ({
  position: 'fixed',
  width: '600px', // 初期サイズ: 4:3のアスペクト比
  height: '450px', // 初期サイズ: 4:3のアスペクト比
  minWidth: '400px',
  minHeight: '300px',
  maxWidth: '90vw',
  maxHeight: '90vh',
  resize: 'both',
  overflow: 'hidden',
  zIndex: 1200,
  backgroundColor: 'white',
  boxShadow:
    '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)',
  borderRadius: '4px',
}))

const DraggableHeader = styled('div')(() => ({
  cursor: 'move',
  padding: '16px 24px 8px 24px',
  backgroundColor: 'white',
  color: colorPalette.monotone[9],
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  userSelect: 'none',
}))

const ContentContainer = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100% - 64px)', // ヘッダーの高さを除く
  overflow: 'hidden',
}))

const FilterContainer = styled(Box)(() => ({
  padding: '7px 16px 0 16px',
  backgroundColor: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}))

const StyledTableContainer = styled(TableContainer)(() => ({
  flex: 1,
  overflow: 'auto',
  padding: '16px',
}))

export const EstimatesByAssigneePopup: React.FC<
  EstimatesByAssigneePopupProps
> = ({
  open,
  onClose,
  getSprintEstimates,
  initialSprintId,
  data,
  dataVersion,
  workloadUnit,
  workloadStandard,
}) => {
  const intl = useIntl()
  const [position, setPosition] = useState({ x: 100, y: 150 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null)
  const [showFinishedAndCanceled, setShowFinishedAndCanceled] =
    useState<boolean>(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // 全スプリント（完了・中止含む）を取得
  const allSprintEstimates = useMemo(() => {
    if (!open) return []
    return getSprintEstimates(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, getSprintEstimates, data, dataVersion])

  // 有効なスプリント一覧（完了・中止を除く）
  const activeSprintEstimates = useMemo(() => {
    if (!open) return []
    return getSprintEstimates(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, getSprintEstimates, data, dataVersion])

  // フィルター用スプリント一覧（スイッチの状態に応じてフィルタリング）
  const sprintEstimates = useMemo(() => {
    return showFinishedAndCanceled ? allSprintEstimates : activeSprintEstimates
  }, [showFinishedAndCanceled, allSprintEstimates, activeSprintEstimates])

  // --- スプリント選択ロジック ---
  // 優先順位: ユーザー選択 > initialSprintId > 先頭の有効なスプリント
  const displaySprintId = useMemo(() => {
    if (selectedSprintId !== null) return selectedSprintId
    if (initialSprintId) return initialSprintId
    return activeSprintEstimates[0]?.sprintId || ''
  }, [selectedSprintId, initialSprintId, activeSprintEstimates])

  // 表示中のスプリントが完了・中止済みかどうか（スイッチ状態の制御に使用）
  const isDisplayingInactiveSprint = useMemo(() => {
    if (!displaySprintId || !open) return false
    return !activeSprintEstimates.some(s => s.sprintId === displaySprintId)
  }, [open, displaySprintId, activeSprintEstimates])

  // ポップアップが閉じた時、または別のスプリントアイコンがクリックされた時に選択状態をリセット
  useEffect(() => {
    setSelectedSprintId(null)
  }, [open, initialSprintId])

  // Escキーでポップアップを閉じる
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && open) {
        onClose()
      }
    }

    if (open) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onClose])

  const handleMouseDown = useCallback((event: React.MouseEvent) => {
    // バツボタンがクリックされた場合はドラッグを開始しない
    if (
      event.target instanceof Element &&
      event.target.closest('[aria-label="close"]')
    ) {
      return
    }

    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      setDragOffset({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      })
      setIsDragging(true)
      event.preventDefault() // デフォルトの動作を防ぐ
    }
  }, [])

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: event.clientX - dragOffset.x,
          y: event.clientY - dragOffset.y,
        })
      }
    },
    [isDragging, dragOffset]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  // フィルタリングされたスプリントデータ（単一選択のため、選択されたスプリントのみ）
  const filteredSprintEstimates = useMemo(() => {
    return allSprintEstimates.filter(
      sprint => sprint.sprintId === displaySprintId
    )
  }, [allSprintEstimates, displaySprintId])

  // 選択中のスプリントオブジェクト（Autocompleteのoptionsと同じsprintEstimatesから検索）
  const selectedSprint = useMemo(() => {
    // まずsprintEstimatesから検索（通常ケース）
    const found = sprintEstimates.find(s => s.sprintId === displaySprintId)
    if (found) return found
    // 完了・中止スプリントの場合はallSprintEstimatesから検索
    return allSprintEstimates.find(s => s.sprintId === displaySprintId)
  }, [sprintEstimates, allSprintEstimates, displaySprintId])

  const handleSprintFilterChange = useCallback(
    (_: React.SyntheticEvent, value: SprintEstimate | null) => {
      setSelectedSprintId(value?.sprintId || '')
    },
    []
  )

  if (!open) return null

  return (
    <PopupContainer
      ref={containerRef}
      style={{
        left: position.x,
        top: position.y,
      }}
    >
      <DraggableHeader onMouseDown={handleMouseDown}>
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontSize: '17px',
            fontWeight: 600,
            color: colorPalette.monotone[10],
          }}
        >
          {intl.formatMessage({
            id: 'productBacklog.estimatesByAssignee.title',
          })}
        </Typography>
        <CloseIcon onClose={onClose} size="m" />
      </DraggableHeader>
      <ContentContainer>
        <FilterContainer>
          <Autocomplete
            key={displaySprintId || 'default'}
            value={selectedSprint}
            options={sprintEstimates}
            getOptionLabel={option => option.sprintName}
            onChange={handleSprintFilterChange}
            isOptionEqualToValue={(option, value) =>
              option.sprintId === value.sprintId
            }
            size="small"
            disableClearable={true}
            autoHighlight={true}
            sx={{ width: 250 }}
            renderInput={params => (
              <TextField
                {...params}
                label={intl.formatMessage({
                  id: 'productBacklog.estimatesByAssignee.filterBySprintLabel',
                })}
                variant="outlined"
              />
            )}
            componentsProps={{
              popper: {
                placement: 'bottom-end',
              },
            }}
            ListboxProps={{
              style: {
                maxHeight: 300,
              },
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={
                  !showFinishedAndCanceled && !isDisplayingInactiveSprint
                }
                onChange={e => {
                  setShowFinishedAndCanceled(!e.target.checked)
                }}
                color="primary"
                size="small"
              />
            }
            label={intl.formatMessage({
              id: 'productBacklog.estimatesByAssignee.hideFinishedAndCanceled',
            })}
            sx={{
              color: colorPalette.monotone[9],
              fontSize: '12px',
              mr: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '12px',
              },
            }}
          />
        </FilterContainer>
        <StyledTableContainer>
          {filteredSprintEstimates.map((sprint, index) => (
            <MemberEstimatedHoursPerSprintTable
              key={sprint.sprintId}
              sprint={sprint}
              isLast={index === filteredSprintEstimates.length - 1}
              workloadUnit={workloadUnit}
              workloadStandard={workloadStandard}
            />
          ))}
        </StyledTableContainer>
      </ContentContainer>
    </PopupContainer>
  )
}
